import { ApolloError } from "@apollo/client";
import { DateTime } from "luxon";

export type ErrorType =
  | "NETWORK_ERROR" // Connection/timeout issues
  | "GRAPHQL_ERROR" // Server/query errors
  | "AUTHORIZATION_ERROR" // 401/403 errors
  | "VALIDATION_ERROR" // Form/input validation
  | "BUSINESS_ERROR" // Domain logic errors
  | "UNKNOWN_ERROR"; // Fallback

export interface StandardError {
  type: ErrorType;
  message: string;
  originalError?: Error;
  code?: string;
  retryable?: boolean;
}

/**
 * Transforms Apollo GraphQL errors into standardized error objects
 * with user-friendly messages and retry information
 */
export const transformApolloError = (apolloError: ApolloError): StandardError => {
  // Network errors (connection issues, timeouts)
  if (apolloError.networkError) {
    return {
      type: "NETWORK_ERROR",
      message: "Unable to connect to server. Please check your connection.",
      originalError: apolloError.networkError,
      retryable: true,
    };
  }

  // GraphQL errors from server
  if (apolloError.graphQLErrors?.length) {
    const gqlError = apolloError.graphQLErrors[0];
    const code = gqlError.extensions?.code as string;

    switch (code) {
      case "UNAUTHENTICATED":
        return {
          type: "AUTHORIZATION_ERROR",
          message: "Your session has expired. Please sign in again.",
          code,
          retryable: false,
        };

      case "FORBIDDEN":
        return {
          type: "AUTHORIZATION_ERROR",
          message: "You do not have permission to perform this action.",
          code,
          retryable: false,
        };

      case "VALIDATION_ERROR":
      case "BAD_USER_INPUT":
        return {
          type: "VALIDATION_ERROR",
          message: gqlError.message || "Please check your input and try again.",
          code,
          retryable: false,
        };

      case "BUSINESS_LOGIC_ERROR":
        return {
          type: "BUSINESS_ERROR",
          message: gqlError.message || "This action cannot be completed.",
          code,
          retryable: false,
        };

      case "INTERNAL_SERVER_ERROR":
        return {
          type: "GRAPHQL_ERROR",
          message: "A server error occurred. Please try again.",
          code,
          retryable: true,
        };

      default:
        return {
          type: "GRAPHQL_ERROR",
          message: gqlError.message || "An error occurred while processing your request.",
          code,
          retryable: true,
        };
    }
  }

  // Fallback for unknown errors
  return {
    type: "UNKNOWN_ERROR",
    message: "An unexpected error occurred. Please try again.",
    originalError: apolloError,
    retryable: true,
  };
};

/**
 * Converts technical error messages into user-friendly messages
 * based on error type and context
 */
export const getUserFriendlyErrorMessage = (error: StandardError): string => {
  switch (error.type) {
    case "NETWORK_ERROR":
      return "Connection problem. Please check your internet and try again.";

    case "AUTHORIZATION_ERROR":
      return error.code === "UNAUTHENTICATED"
        ? "Your session has expired. Please sign in again."
        : "You need permission to access this. Please contact your administrator.";

    case "VALIDATION_ERROR":
      return "Please check your input and try again.";

    case "BUSINESS_ERROR":
      return error.message; // Use the specific business error message

    case "GRAPHQL_ERROR":
      return "A server error occurred. Please try again or contact support if the problem persists.";

    default:
      return "Something went wrong. Please try again or contact support if the problem persists.";
  }
};

/**
 * Determines if an error should show a retry button
 */
export const isRetryableError = (error: StandardError): boolean => {
  return error.retryable === true;
};

/**
 * Gets appropriate error action text based on error type
 */
export const getErrorActionText = (error: StandardError): string => {
  switch (error.type) {
    case "NETWORK_ERROR":
      return "Try Again";
    case "AUTHORIZATION_ERROR":
      return error.code === "UNAUTHENTICATED" ? "Sign In" : "Contact Support";
    case "VALIDATION_ERROR":
      return "Fix Input";
    default:
      return "Retry";
  }
};

/**
 * Logs error to console with structured information for debugging
 */
export const logError = (error: StandardError, context?: string) => {
  const logData = {
    type: error.type,
    message: error.message,
    code: error.code,
    context,
    originalError: error.originalError,
    timestamp: DateTime.now().toISO(),
  };

  console.error(`[${error.type}] ${context || "Error"}:`, logData);

  // TODO: Send to error tracking service (Sentry, LogRocket, etc.)
  // trackError(logData);
};
